import React, { useEffect, useState, useCallback } from "react";
import API from "../../api/axiosConfig";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Pencil, Save, X, ShieldCheck, Users as UsersIcon } from "lucide-react";
import CreateUserModal from "../../components/modals/CreateUserModal";

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

const MODULES = [
  { key: "dashboard",   label: "Dashboard" },
  { key: "pedidos",     label: "Pedidos" },
  { key: "mesas",       label: "Mesas" },
  { key: "mesas_admin", label: "Mesas: crear / editar / eliminar" },
  { key: "caja",        label: "Caja del día" },
  { key: "impresoras",  label: "Impresoras" },
  { key: "productos",   label: "Productos" },
  { key: "sedes",       label: "Sedes" },
  { key: "secciones",   label: "Secciones" },
  { key: "cupones",     label: "Cupones" },
  { key: "ryders",      label: "Ryders" },
  { key: "despacho",    label: "Despacho" },
  { key: "resenas",     label: "Reseñas" },
  { key: "subastas",    label: "Subastas" },
  { key: "usuarios",    label: "Usuarios" },
  { key: "metricas",    label: "Métricas" },
];

function getCompanyId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.companyId ?? u?.company?.id ?? null;
  } catch { return null; }
}

const ROLE_LABELS = {
  admin:   "Administrador",
  company: "Empresa",
  user:    "Usuario",
  ryder:   "Domiciliario",
  delivery: "Empresa de domicilios",
};

const ROLE_EMPTY = { name: "", modules: [] };

export default function CompanyUsers() {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const [tab, setTab] = useState("empleados"); // empleados | roles

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // user | null

  const [roles, setRoles] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [roleModal, setRoleModal] = useState(null); // null | 'create' | role
  const [roleForm, setRoleForm] = useState(ROLE_EMPTY);
  const [savingRole, setSavingRole] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!companyId) return;
    setLoadingUsers(true);
    try {
      const res = await API.get(`/companies/${companyId}/users`);
      setUsers(res.data?.data?.users ?? []);
    } catch {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoadingUsers(false);
    }
  }, [companyId]);

  const fetchRoles = useCallback(async () => {
    if (!companyId) return;
    setLoadingRoles(true);
    try {
      const res = await API.get("/company-roles", { params: { companyId } });
      setRoles(res.data?.data?.roles ?? []);
    } catch {
      toast.error("Error al cargar roles");
    } finally {
      setLoadingRoles(false);
    }
  }, [companyId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => {
    if (!companyId) return;
    API.get(`/sedes/company/${companyId}`)
      .then((res) => setSedes(res.data?.data?.sedes ?? res.data?.data ?? []))
      .catch(() => {});
  }, [companyId]);
  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleDelete = async (userId) => {
    if (!window.confirm("¿Eliminar este usuario de la empresa?")) return;
    try {
      await API.delete(`/companies/${companyId}/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al eliminar usuario");
    }
  };

  const openEditUser = (u) => setEditingUser({ ...u, companyRoleId: u.companyRoleId ?? "", sedeId: u.sedeId ?? "" });

  const handleSaveUser = async () => {
    try {
      await API.patch(`/companies/${companyId}/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        companyRoleId: editingUser.companyRoleId || null,
        ...(editingUser.isOwner ? {} : { sedeId: editingUser.sedeId || null }),
      });
      toast.success("Usuario actualizado");
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al actualizar usuario");
    }
  };

  const openCreateRole = () => { setRoleForm(ROLE_EMPTY); setRoleModal("create"); };
  const openEditRole = (r) => { setRoleForm({ name: r.name, modules: r.modules ?? [] }); setRoleModal(r); };
  const closeRoleModal = () => { setRoleModal(null); setRoleForm(ROLE_EMPTY); };

  const toggleModule = (key) => {
    setRoleForm((f) => ({
      ...f,
      modules: f.modules.includes(key) ? f.modules.filter((m) => m !== key) : [...f.modules, key],
    }));
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) { toast.warn("El nombre del rol es requerido"); return; }
    setSavingRole(true);
    try {
      if (roleModal === "create") {
        await API.post("/company-roles", { companyId, ...roleForm });
        toast.success("Rol creado");
      } else {
        await API.patch(`/company-roles/${roleModal.id}`, roleForm);
        toast.success("Rol actualizado");
      }
      await fetchRoles();
      closeRoleModal();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
    finally { setSavingRole(false); }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`¿Eliminar el rol "${role.name}"? Los empleados que lo tengan quedarán sin rol (acceso completo).`)) return;
    try {
      await API.delete(`/company-roles/${role.id}`);
      toast.success("Rol eliminado");
      await Promise.all([fetchRoles(), fetchUsers()]);
    } catch { toast.error("Error al eliminar el rol"); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-supernova-600 hover:text-supernova-800 mb-6 font-semibold transition"
      >
        <ArrowLeft size={20} />
        Volver
      </button>

      <h2 className="text-2xl font-bold mb-5 flex items-center gap-2">
        <UsersIcon className="text-supernova-600" size={26} /> Usuarios y permisos
      </h2>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl mb-6 max-w-md">
        {[{ id: "empleados", label: "Empleados" }, { id: "roles", label: "Roles y permisos" }].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-4 py-2 rounded-xl text-sm font-semibold transition ${
              tab === t.id ? "bg-white shadow text-gray-800" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "empleados" && (
        <>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowCreateModal(true)} className={`${btn} bg-supernova-500 hover:bg-supernova-600 text-white`}>
              <Plus size={16} /> Nuevo Usuario
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[680px] text-left">
              <thead className="bg-supernova-100 text-gray-700">
                <tr>
                  <th className="py-3 px-4">Nombre</th>
                  <th className="py-3 px-4">Correo</th>
                  <th className="py-3 px-4">Rol de plataforma</th>
                  <th className="py-3 px-4">Rol de empresa</th>
                  <th className="py-3 px-4">Sede</th>
                  <th className="py-3 px-4 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {loadingUsers ? (
                  <tr><td colSpan={6} className="text-center py-6 text-gray-400 text-sm">Cargando...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-gray-500 text-sm">No hay usuarios registrados.</td></tr>
                ) : (
                  users.map((u, index) => (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-t hover:bg-supernova-50"
                    >
                      <td className="py-3 px-4 font-medium text-gray-800">{u.name}</td>
                      <td className="py-3 px-4 text-gray-600">{u.email}</td>
                      <td className="py-3 px-4 text-gray-500">{ROLE_LABELS[u.role] ?? u.role}</td>
                      <td className="py-3 px-4 text-gray-500">{u.companyRole?.name ?? "Todos los módulos"}</td>
                      <td className="py-3 px-4 text-gray-500">
                        {u.isOwner ? "Todas las sedes" : u.sede?.name ? u.sede.name : <span className="text-red-500 font-semibold">Sin sede</span>}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEditUser(u)} className="text-gray-400 hover:text-supernova-600 transition" title="Editar">
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => handleDelete(u.id)} className="text-gray-300 hover:text-red-500 transition" title="Eliminar usuario">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "roles" && (
        <>
          <div className="flex justify-end mb-4">
            <button onClick={openCreateRole} className={`${btn} bg-supernova-500 hover:bg-supernova-600 text-white`}>
              <Plus size={16} /> Nuevo rol
            </button>
          </div>

          {loadingRoles ? (
            <div className="text-center py-10 text-gray-400 text-sm">Cargando...</div>
          ) : roles.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShieldCheck size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin roles configurados — todos los empleados tienen acceso completo</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {roles.map((r) => (
                <div key={r.id} className="border border-gray-200 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-bold text-gray-800 text-sm">{r.name}</p>
                    <span className="text-xs text-gray-400">{r.modules.length} módulo{r.modules.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {r.modules.length === 0 ? (
                      <span className="text-xs text-gray-400">Sin módulos asignados</span>
                    ) : (
                      r.modules.map((m) => (
                        <span key={m} className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {MODULES.find((mod) => mod.key === m)?.label ?? m}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEditRole(r)} className={`${btn} text-xs bg-gray-50 text-gray-600 hover:bg-gray-100`}>
                      <Pencil size={11} /> Editar
                    </button>
                    <button onClick={() => handleDeleteRole(r)} className={`${btn} text-xs bg-red-50 text-red-500 hover:bg-red-100 ml-auto`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <CreateUserModal
          companyId={companyId}
          roles={roles}
          sedes={sedes}
          onClose={() => setShowCreateModal(false)}
          onUserCreated={fetchUsers}
        />
      )}

      {/* Edit user modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditingUser(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Editar usuario</h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre</label>
                <input className={inp} value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Correo</label>
                <input type="email" className={inp} value={editingUser.email ?? ""} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Rol de empresa</label>
                <select
                  className={inp}
                  value={editingUser.companyRoleId}
                  onChange={(e) => setEditingUser({ ...editingUser, companyRoleId: e.target.value })}
                >
                  <option value="">Sin rol (todos los módulos)</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              {!editingUser.isOwner && (
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Sede *</label>
                  <select
                    className={inp}
                    value={editingUser.sedeId}
                    onChange={(e) => setEditingUser({ ...editingUser, sedeId: e.target.value })}
                  >
                    <option value="">Selecciona una sede</option>
                    {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditingUser(null)} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSaveUser} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
                <Save size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role modal */}
      {roleModal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={closeRoleModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md my-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">{roleModal === "create" ? "Nuevo rol" : "Editar rol"}</h3>
              <button onClick={closeRoleModal} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                <input className={inp} value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="Mesero, Cajero..." />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-2 block">Módulos permitidos</label>
                <div className="grid grid-cols-2 gap-2">
                  {MODULES.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleForm.modules.includes(m.key)}
                        onChange={() => toggleModule(m.key)}
                        className="accent-supernova-500"
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeRoleModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSaveRole} disabled={savingRole} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
                <Save size={14} /> {savingRole ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
